import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ILoginImageItem, ISiteImageItem } from "api/statics/statics.type";

// App-wide showcase imagery authored in backoffice "Site Görselleri" (served by /v1/statics/site_images).
// Every consumer keeps its bundled asset as the fallback (`useSiteImage(slot, bundled)`), so an empty
// map — before the boot fetch, or after it failed — renders exactly what the app rendered before.
export interface SiteImageEntry {
  url: string;
  posterUrl: string | null;
  mediaType: "image" | "video";
}

interface SiteImagesState {
  /** slot_key -> the asset to render instead of the bundled one. */
  images: Record<string, SiteImageEntry>;
  /** Preview only: the login carousel with backoffice drafts applied; null = use /login_images. */
  previewLoginImages: ILoginImageItem[] | null;
  /** A backoffice preview link is active in this tab. */
  previewActive: boolean;
  previewExpiresAt: string | null;
  /** The tab's preview link was refused (expired / unknown) — the banner says so until dismissed. */
  previewExpired: boolean;
}

const initialState: SiteImagesState = {
  images: {},
  previewLoginImages: null,
  previewActive: false,
  previewExpiresAt: null,
  previewExpired: false,
};

const toMap = (items: ISiteImageItem[]): Record<string, SiteImageEntry> => Object.fromEntries(items.map((i) => [i.slot_key, { url: i.url, posterUrl: i.poster_url, mediaType: i.media_type }]));

export const siteImagesSlice = createSlice({
  name: "siteImages",
  initialState,
  reducers: {
    setSiteImages: (state, action: PayloadAction<ISiteImageItem[]>) => {
      state.images = toMap(action.payload);
    },
    setSitePreview: (state, action: PayloadAction<{ images: ISiteImageItem[]; loginImages: ILoginImageItem[]; expiresAt: string }>) => {
      state.images = toMap(action.payload.images);
      state.previewLoginImages = action.payload.loginImages;
      state.previewActive = true;
      state.previewExpiresAt = action.payload.expiresAt;
    },
    clearSitePreview: (state) => {
      state.previewLoginImages = null;
      state.previewActive = false;
      state.previewExpiresAt = null;
    },
    // Said in the banner, not a snackbar: the snackbar renderer lives in the signed-in layouts, and a
    // preview link is as likely to land on the login or Academy screens, which have none.
    markSitePreviewExpired: (state) => {
      state.previewLoginImages = null;
      state.previewActive = false;
      state.previewExpiresAt = null;
      state.previewExpired = true;
    },
    dismissSitePreviewExpired: (state) => {
      state.previewExpired = false;
    },
  },
});

export const { setSiteImages, setSitePreview, clearSitePreview, markSitePreviewExpired, dismissSitePreviewExpired } = siteImagesSlice.actions;

export default siteImagesSlice.reducer;
